import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Button, Select, Space, Typography, Table,
  Tag, Timeline, message, Tabs, Row, Col, Divider, AutoComplete
} from 'antd';
import {
  SendOutlined, SyncOutlined, ClockCircleOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined, MessageOutlined
} from '@ant-design/icons';
// import { useI18n } from '../hooks/useI18n'; // 暂时注释掉，因为当前没有使用国际化
import { WhatsAppAccount, AgentPlatform, WhatsAppAccountMapping } from '../services/whatsappConnectorService';

// 辅助函数：创建带认证头的fetch请求
const createAuthenticatedFetch = (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  const currentTenant = localStorage.getItem('currentTenant') || 'default';
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  headers['X-Tenant-Code'] = currentTenant;
  
  return fetch(url, {
    ...options,
    headers
  });
};

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

// 发送历史记录接口
interface SendHistoryRecord {
  id: string;
  timestamp: string;
  accountId: number;
  to: string;
  message: string;
  status: 'sending' | 'success' | 'failed';
  error?: string;
}

interface TestMessage {
  id: string;
  timestamp: string;
  type: 'request' | 'response' | 'error' | 'info';
  step: string;
  data: unknown;
  status: 'pending' | 'success' | 'error' | 'info';
}

// Partner 类型定义
type PartnerType = 'twilio' | 'dialog360' | 'aisensy';

interface WebhookPayload {
  object: string;
  entry: Array<{
    id: string;
    changes: Array<{
      value: {
        messaging_product: string;
        metadata: {
          display_phone_number: string;
          phone_number_id: string;
        };
        contacts: Array<{
          profile: {
            name: string;
          };
          wa_id: string;
        }>;
        messages: Array<{
          from: string;
          id: string;
          timestamp: string;
          text?: {
            body: string;
          };
          type: string;
        }>;
      };
      field: string;
    }>;
  }>;
}

const WhatsAppConnectorTestPage: React.FC = () => {
  // const { t } = useI18n(); // 暂时注释掉，因为当前没有使用国际化
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<WhatsAppAccount[]>([]);
  const [platforms, setPlatforms] = useState<AgentPlatform[]>([]);
  const [mappings, setMappings] = useState<WhatsAppAccountMapping[]>([]);
  const [testMessages, setTestMessages] = useState<TestMessage[]>([]);
  const [currentTestId, setCurrentTestId] = useState<string>('');

  // 发送消息相关状态
  const [sendForm] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [sendHistory, setSendHistory] = useState<SendHistoryRecord[]>([]);

  // 监听消息类型变化
  const messageType = Form.useWatch('messageType', form);

  // 加载配置数据
  useEffect(() => {
    loadConfigData();
  }, []);

  const loadConfigData = async () => {
    try {
      const tenant = localStorage.getItem('currentTenant') || 'default';

      const [accountsRes, platformsRes, mappingsRes] = await Promise.all([
        createAuthenticatedFetch(`/api/${tenant}/whatsappapi/accounts`),
        createAuthenticatedFetch(`/api/${tenant}/whatsappapi/platforms`),
        createAuthenticatedFetch(`/api/${tenant}/whatsapp/mappings`)
      ]);

      const accountsData = accountsRes.ok ? await accountsRes.json() : { data: [] };
      const platformsData = platformsRes.ok ? await platformsRes.json() : { data: [] };
      const mappingsData = mappingsRes.ok ? await mappingsRes.json() : { data: [] };

      setAccounts(accountsData.data || []);
      setPlatforms(platformsData.data || []);
      setMappings(mappingsData.data || []);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      message.error(`加载配置数据失败: ${errorMessage}`);
    }
  };

  const addTestMessage = (step: string, type: TestMessage['type'], data: unknown, status: TestMessage['status'] = 'info') => {
    const message: TestMessage = {
      id: `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
      timestamp: new Date().toLocaleString(),
      type,
      step,
      data,
      status
    };
    setTestMessages(prev => [...prev, message]);
  };

  // 发送消息处理函数
  const handleSendMessage = async (values: Record<string, unknown>) => {
    const accountId = values.accountId as number;
    const to = values.to as string;
    const messageContent = values.message as string;
    const sendId = `send_${Date.now()}`;
    const tenant = localStorage.getItem('currentTenant') || 'default';

    try {
      setSending(true);

      // 添加到发送历史
      const sendRecord: SendHistoryRecord = {
        id: `send_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
        timestamp: new Date().toLocaleString(),
        accountId,
        to,
        message: messageContent,
        status: 'sending'
      };

      setSendHistory(prev => [sendRecord, ...prev]);

      // 通过后台服务调用发送消息的 API
      const response = await createAuthenticatedFetch(`/api/${tenant}/whatsappapi/send/${accountId}`, {
        method: 'POST',
        body: JSON.stringify({ to, message: messageContent })
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }

      // 更新发送历史状态
      setSendHistory(prev =>
        prev.map(record =>
          record.id === sendId
            ? { ...record, status: 'success', timestamp: new Date().toLocaleString() }
            : record
        )
      );

      message.success('消息发送成功');
      sendForm.resetFields();

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      // 更新发送历史状态为失败
      setSendHistory(prev =>
        prev.map(record =>
          record.id === sendId
            ? { ...record, status: 'failed', error: errorMessage, timestamp: new Date().toLocaleString() }
            : record
        )
      );

      message.error(`发送失败: ${errorMessage}`);
    } finally {
      setSending(false);
    }
  };

  const generateTestMessage = (accountId: string, messageText: string, partner: PartnerType, messageType: string, waId: string, profileName: string, mediaUrl?: string): unknown => {
    const account = accountId ? accounts.find(acc => acc.id === Number(accountId)) : null;
    
    // 如果没有找到账号，使用默认值
    const defaultAccount = {
      phone_number: '+14155238886',
      account_name: 'test_account',
      business_account_id: 'test_business_account_id'
    };
    
    const selectedAccount = account || defaultAccount;

    if (partner === 'twilio') {
      // Twilio webhook 格式 - 基于真实数据格式
      const baseMessage = {
        SmsMessageSid: `SM${Date.now()}${Math.random().toString(36).substring(2, 15)}`,
        ProfileName: profileName,
        MessageType: messageType,
        SmsSid: `SM${Date.now()}${Math.random().toString(36).substring(2, 15)}`,
        WaId: waId,
        SmsStatus: "received",
        Body: messageType === 'text' ? messageText : "",
        To: `whatsapp:${selectedAccount.phone_number}`,
        NumSegments: "1",
        ReferralNumMedia: "0",
        MessageSid: `SM${Date.now()}${Math.random().toString(36).substring(2, 15)}`,
        AccountSid: `AC${Math.random().toString(36).substring(2, 15)}`,
        ChannelMetadata: JSON.stringify({
          type: "whatsapp",
          data: {
            context: {
              ProfileName: profileName,
              WaId: waId
            }
          }
        }),
        From: waId,
        ApiVersion: "2010-04-01"
      };

      // 根据消息类型添加媒体信息
      if (messageType === 'document' || messageType === 'image') {
        return {
          ...baseMessage,
          NumMedia: "1",
          MediaContentType0: messageType === 'document' ? "application/pdf" : "image/jpeg",
          MediaUrl0: mediaUrl || "https://api.twilio.com/2010-04-01/Accounts/ACf97abe1a708051aa75f6619ecd50d355/Messages/MM2d3e5b9f5ada9c8dcb502c7ae6cbc41f/Media/MEebdf9ff64cfce7a067f1f502cc4a5305"
        };
      } else {
        return {
          ...baseMessage,
          NumMedia: "0"
        };
      }
    } else if (partner === 'dialog360') {
      // Dialog360 webhook 格式 (类似标准 WhatsApp Business API)
      return {
        object: "whatsapp_business_account",
        entry: [
          {
            id: selectedAccount.business_account_id || "test_business_account_id",
            changes: [
              {
                value: {
                  messaging_product: "whatsapp",
                  metadata: {
                    display_phone_number: selectedAccount.phone_number,
                    phone_number_id: selectedAccount.account_name
                  },
                  contacts: [
                    {
                      profile: {
                        name: "Test User"
                      },
                      wa_id: "1234567890"
                    }
                  ],
                  messages: [
                    {
                      from: "1234567890",
                      id: `test_msg_${Date.now()}`,
                      timestamp: Math.floor(Date.now() / 1000).toString(),
                      text: {
                        body: messageText
                      },
                      type: "text"
                    }
                  ]
                },
                field: "messages"
              }
            ]
          }
        ]
      };
    } else if (partner === 'aisensy') {
      // AiSensy webhook 格式 (类似标准 WhatsApp Business API)
      return {
        object: "whatsapp_business_account",
        entry: [
          {
            id: selectedAccount.business_account_id || "test_business_account_id",
            changes: [
              {
                value: {
                  messaging_product: "whatsapp",
                  metadata: {
                    display_phone_number: selectedAccount.phone_number,
                    phone_number_id: selectedAccount.account_name
                  },
                  contacts: [
                    {
                      profile: {
                        name: "Test User"
                      },
                      wa_id: "1234567890"
                    }
                  ],
                  messages: [
                    {
                      from: "1234567890",
                      id: `test_msg_${Date.now()}`,
                      timestamp: Math.floor(Date.now() / 1000).toString(),
                      text: {
                        body: messageText
                      },
                      type: "text"
                    }
                  ]
                },
                field: "messages"
              }
            ]
          }
        ]
      };
    }

    // 默认返回标准 WhatsApp Business API 格式
    return {
      object: "whatsapp_business_account",
      entry: [
        {
          id: selectedAccount.business_account_id || "test_business_account_id",
          changes: [
            {
              value: {
                messaging_product: "whatsapp",
                metadata: {
                  display_phone_number: selectedAccount.phone_number,
                  phone_number_id: selectedAccount.account_name
                },
                contacts: [
                  {
                    profile: {
                      name: "Test User"
                    },
                    wa_id: "1234567890"
                  }
                ],
                messages: [
                  {
                    from: "1234567890",
                    id: `test_msg_${Date.now()}`,
                    timestamp: Math.floor(Date.now() / 1000).toString(),
                    text: {
                      body: messageText
                    },
                    type: "text"
                  }
                ]
              },
              field: "messages"
            }
          ]
        }
      ]
    };
  };

  const simulateWebhookCall = async (values: Record<string, unknown>) => {
    const accountId = values.accountId as string;
    const messageText = values.messageText as string;
    const processingMode = values.processingMode as string;
    const partner = values.partner as PartnerType;
    const messageType = values.messageType as string;
    const waId = values.waId as string || '8615002185018';
    const profileName = values.profileName as string || 'Ethan';
    const mediaUrl = values.mediaUrl as string;
    const testId = `test_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    setCurrentTestId(testId);

    setLoading(true);
    addTestMessage('开始测试', 'info', { testId, accountId, messageText, partner, messageType, waId, profileName }, 'pending');

    try {
      // 验证必填字段
      if (messageType === 'text' && !messageText) {
        addTestMessage('验证失败', 'error', { error: '文本消息内容不能为空' }, 'error');
        throw new Error('文本消息内容不能为空');
      }
      if ((messageType === 'document' || messageType === 'image') && !mediaUrl) {
        addTestMessage('验证失败', 'error', { error: '媒体消息需要提供媒体URL' }, 'error');
        throw new Error('媒体消息需要提供媒体URL');
      }

      if (!partner) {
        addTestMessage('验证失败', 'error', { error: '请选择 Partner 服务商' }, 'error');
        throw new Error('请选择 Partner 服务商');
      }

      // 1. 生成测试消息
      const webhookPayload = generateTestMessage(accountId, messageText, partner, messageType, waId, profileName, mediaUrl);
      addTestMessage('生成Webhook负载', 'request', webhookPayload, 'success');

      // 2. 查找映射关系（如果提供了 accountId）
      let account: WhatsAppAccount | null = null;
      let mapping: WhatsAppAccountMapping | null = null;
      
      if (accountId) {
        account = accounts.find(acc => acc.id === Number(accountId)) || null;
        mapping = mappings.find(m => m.whatsapp_account_id === Number(accountId)) || null;
        
        addTestMessage('查找账号信息', 'info', {
          accountId,
          accountFound: !!account,
          mappingFound: !!mapping
        }, 'success');
      } else {
        addTestMessage('跳过账号映射', 'info', { 
          reason: '未选择 WhatsApp 账号，将使用默认配置' 
        }, 'success');
      }

      // 3. 处理映射关系（如果有的话）
      if (mapping) {
        addTestMessage('查找映射关系', 'info', {
          whatsappAccount: account?.account_name,
          serviceId: mapping.service_id,
          agentId: mapping.agent_id,
          agentName: mapping.agent_name,
          processingMode: mapping.processing_mode
        }, 'success');

        // 查找平台配置
        const platform = platforms.find(p => p.id === mapping.service_id);
        if (platform) {
          addTestMessage('查找平台配置', 'info', {
            platformName: platform.platform_name,
            platformType: platform.platform_type,
            baseUrl: platform.base_url
          }, 'success');
        } else {
        addTestMessage('查找平台配置', 'info', { 
          serviceId: mapping.service_id, 
          message: '未找到平台配置，将使用默认处理' 
        }, 'pending');
        }
      } else {
        addTestMessage('映射关系', 'info', { 
          message: '未找到映射关系，将直接调用 webhook 进行测试' 
        }, 'success');
      }

      // 4. 根据选择的 Partner 调用相应的 Webhook
      const webhookUrl = `/api/default/whatsappapi/webhook/${partner}`;

      addTestMessage('发送到Connector Webhook', 'request', {
        url: webhookUrl,
        method: 'POST',
        partner,
        payload: webhookPayload
      }, 'pending');

      try {
        // 调用后台服务，后台服务会调用注册的WhatsApp Connector服务
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(webhookPayload)
        });

        if (response.ok) {
          const result = await response.json();
          addTestMessage('Webhook响应', 'response', result, 'success');
        } else {
          const error = await response.text();
          addTestMessage('Webhook响应', 'error', { status: response.status, error }, 'error');
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : '未知错误';
        addTestMessage('Webhook调用失败', 'error', { error: errorMessage }, 'error');
        // 如果调用失败，仍然模拟Connector的处理逻辑用于演示
        if (mapping) {
          const platform = platforms.find(p => p.id === mapping.service_id);
          if (platform) {
            simulateConnectorProcessing(accountId, webhookPayload, mapping, platform, processingMode);
          }
        }
      }

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      addTestMessage('测试失败', 'error', { error: errorMessage }, 'error');
    } finally {
      setLoading(false);
    }
  };

  const simulateConnectorProcessing = (
    accountId: string,
    webhookPayload: unknown,
    mapping: WhatsAppAccountMapping,
    platform: AgentPlatform,
    processingMode: string
  ) => {
    // 模拟Connector内部处理
    addTestMessage('Connector处理开始', 'info', { accountId, processingMode }, 'pending');

    // 解析消息 - 需要类型断言，因为我们已经知道这是标准格式
    const payload = webhookPayload as WebhookPayload;
    const message = payload.entry[0].changes[0].value.messages[0];
    addTestMessage('解析消息内容', 'info', {
      from: message.from,
      type: message.type,
      content: message.text?.body,
      timestamp: message.timestamp
    }, 'success');

    // 检查映射关系
    addTestMessage('验证映射关系', 'info', {
      whatsappAccountId: mapping.whatsapp_account_id,
      serviceId: mapping.service_id,
      agentId: mapping.agent_id,
      processingMode: mapping.processing_mode
    }, 'success');

    // 模拟调用Agent平台API
    addTestMessage('调用Agent平台API', 'request', {
      platform: platform.platform_name,
      agentId: mapping.agent_id,
      baseUrl: platform.base_url,
      message: message.text?.body
    }, 'pending');

    // 模拟Agent响应
    setTimeout(() => {
      if (processingMode === 'sync') {
        // 同步模式
        addTestMessage('Agent同步响应', 'response', {
          agentResponse: `Hello! I received your message: "${message.text?.body}". This is a simulated response from ${mapping.agent_name} on ${platform.platform_name}.`,
          processingTime: '150ms'
        }, 'success');

        // 发送回WhatsApp
        addTestMessage('发送响应到WhatsApp', 'request', {
          to: message.from,
          content: 'Response sent successfully'
        }, 'success');

      } else {
        // 异步模式
        addTestMessage('Agent异步处理开始', 'info', {
          taskId: `async_task_${Date.now()}`,
          status: 'processing'
        }, 'pending');

        // 模拟异步处理
        setTimeout(() => {
          addTestMessage('Agent异步响应完成', 'response', {
            taskId: `async_task_${Date.now()}`,
            agentResponse: `Async response to: "${message.text?.body}". This response was processed asynchronously by ${mapping.agent_name}.`,
            processingTime: '2.5s'
          }, 'success');

          // 发送回WhatsApp
          addTestMessage('异步响应发送到WhatsApp', 'request', {
            to: message.from,
            content: 'Async response sent successfully'
          }, 'success');
        }, 2500);
      }
    }, 1000);
  };

  const clearTestMessages = () => {
    setTestMessages([]);
    setCurrentTestId('');
  };

  const getStatusIcon = (status: TestMessage['status']) => {
    switch (status) {
      case 'success': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error': return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'pending': return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      default: return <SyncOutlined spin />;
    }
  };

  const getStatusColor = (type: TestMessage['type']) => {
    switch (type) {
      case 'request': return 'blue';
      case 'response': return 'green';
      case 'error': return 'red';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
    },
    {
      title: '步骤',
      dataIndex: 'step',
      key: 'step',
      width: 150,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: TestMessage['type']) => (
        <Tag color={getStatusColor(type)}>{type.toUpperCase()}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: TestMessage['status']) => (
        <Space>
          {getStatusIcon(status)}
          <span>{status}</span>
        </Space>
      ),
    },
    {
      title: '详情',
      dataIndex: 'data',
      key: 'data',
      render: (data: unknown) => (
        <pre style={{ fontSize: '12px', maxHeight: '100px', overflow: 'auto' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Title level={2}>WhatsApp Connector 测试页面</Title>
      <Paragraph>
        这个页面用于测试 WhatsApp Connector 的完整消息处理流程，包括 webhook 数据接收、
        账号映射路由、Agent平台调用以及响应发送等各个环节。
      </Paragraph>

      <Divider />

      <Tabs defaultActiveKey="receive" type="card" size="large">
        <TabPane
          tab={
            <span>
              <MessageOutlined />
              接收 WhatsApp 消息测试
            </span>
          }
          key="receive"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Card title="测试配置" style={{ marginBottom: '16px' }}>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={simulateWebhookCall}
                  initialValues={{
                    processingMode: 'sync',
                    partner: 'twilio',
                    messageType: 'text',
                    waId: '+8615002185028',
                    profileName: 'Ethan'
                  }}
                >
                  <Form.Item
                    name="accountId"
                    label="WhatsApp 账号"
                  >
                    <Select placeholder="选择测试账号">
                      {accounts.map(account => (
                        <Select.Option key={account.id} value={account.id}>
                          {account.account_name} ({account.phone_number})
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="messageType"
                    label="消息类型"
                    rules={[{ required: true, message: '请选择消息类型' }]}
                    initialValue="text"
                  >
                    <Select placeholder="选择消息类型">
                      <Select.Option value="text">文本消息</Select.Option>
                      <Select.Option value="document">文档消息 (PDF)</Select.Option>
                      <Select.Option value="image">图片消息</Select.Option>
                    </Select>
                  </Form.Item>

                  {messageType === 'text' && (
                    <Form.Item
                      name="messageText"
                      label="测试消息内容"
                      rules={[{ required: true, message: '文本消息内容不能为空' }]}
                    >
                      <TextArea
                        placeholder="输入要发送的测试消息..."
                        rows={3}
                      />
                    </Form.Item>
                  )}

                  {(messageType === 'document' || messageType === 'image') && (
                    <Form.Item
                      name="mediaUrl"
                      label={`${messageType === 'document' ? 'PDF文档' : '图片'}文件URL`}
                      rules={[{ required: true, message: '媒体消息需要提供媒体URL' }]}
                      initialValue={messageType === 'document' ? 'https://api.twilio.com/2010-04-01/Accounts/ACf97abe1a708051aa75f6619ecd50d355/Messages/MM2d3e5b9f5ada9c8dcb502c7ae6cbc41f/Media/MEebdf9ff64cfce7a067f1f502cc4a5305' : ''}
                    >
                      <Input
                        placeholder={`输入${messageType === 'document' ? 'PDF文档' : '图片'}文件URL`}
                      />
                    </Form.Item>
                  )}

                  {(messageType === 'document' || messageType === 'image') && (
                    <Form.Item
                      name="messageText"
                      label="消息描述 (可选)"
                    >
                      <TextArea
                        placeholder="输入消息描述或说明..."
                        rows={2}
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    name="waId"
                    label="发送者手机号 (WaId)"
                    initialValue="+8615002185028"
                    rules={[{ required: true, message: '请输入发送者手机号' }]}
                  >
                    <AutoComplete
                      placeholder="输入或选择发送者手机号"
                      allowClear
                      filterOption={(inputValue, option) =>
                        (option?.value as string).toUpperCase().includes(inputValue.toUpperCase())
                      }
                      options={[
                        { value: '+8615002185028', label: '+8615002185028' },
                        { value: '+8613818791405', label: '+8613818791405' },
                        { value: '13818791405', label: '13818791405' }
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="profileName"
                    label="发送者姓名 (ProfileName)"
                    initialValue="Ethan"
                  >
                    <Input
                      placeholder="输入发送者姓名"
                    />
                  </Form.Item>

                  <Form.Item
                    name="partner"
                    label="Partner 服务商"
                    rules={[{ required: true, message: '请选择 Partner 服务商' }]}
                  >
                    <Select placeholder="选择 Partner 服务商">
                      <Select.Option value="twilio">Twilio</Select.Option>
                      <Select.Option value="dialog360">Dialog360</Select.Option>
                      <Select.Option value="aisensy">AiSensy</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="processingMode"
                    label="处理模式"
                  >
                    <Select>
                      <Select.Option value="sync">同步处理</Select.Option>
                      <Select.Option value="async">异步处理</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />}>
                        {loading ? '测试中...' : '开始测试'}
                      </Button>
                      <Button onClick={clearTestMessages}>
                        清空日志
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Card>

              <Card title="配置信息" style={{ marginBottom: '16px' }}>
                <Tabs defaultActiveKey="accounts" size="small">
                  <TabPane tab={`WhatsApp账号 (${accounts.length})`} key="accounts">
                    {accounts.length === 0 ? (
                      <Text type="secondary">暂无账号配置</Text>
                    ) : (
                      accounts.map(account => (
                        <div key={account.id} style={{ marginBottom: '8px' }}>
                          <Text strong>{account.account_name}</Text>
                          <br />
                          <Text type="secondary">{account.phone_number}</Text>
                        </div>
                      ))
                    )}
                  </TabPane>
                  <TabPane tab={`Agent平台 (${platforms.length})`} key="platforms">
                    {platforms.length === 0 ? (
                      <Text type="secondary">暂无平台配置</Text>
                    ) : (
                      platforms.map(platform => (
                        <div key={platform.id} style={{ marginBottom: '8px' }}>
                          <Text strong>{platform.platform_name}</Text>
                          <br />
                          <Text type="secondary">{platform.platform_type} - {platform.base_url}</Text>
                        </div>
                      ))
                    )}
                  </TabPane>
                  <TabPane tab={`映射关系 (${mappings.length})`} key="mappings">
                    {mappings.length === 0 ? (
                      <Text type="secondary">暂无映射配置</Text>
                    ) : (
                      mappings.map(mapping => (
                        <div key={mapping.id} style={{ marginBottom: '8px' }}>
                          <Text strong>账号 → Agent</Text>
                          <br />
                          <Text type="secondary">
                            {mapping.agent_name} ({mapping.processing_mode})
                          </Text>
                        </div>
                      ))
                    )}
                  </TabPane>
                </Tabs>
              </Card>
            </Col>

            <Col span={12}>
              <Card
                title={`测试日志 (${testMessages.length})`}
                extra={
                  testMessages.length > 0 && (
                    <Text type="secondary">
                      测试ID: {currentTestId}
                    </Text>
                  )
                }
              >
                <Table
                  dataSource={testMessages}
                  columns={columns}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ y: 400 }}
                  locale={{ emptyText: '暂无测试日志' }}
                />
              </Card>
            </Col>
          </Row>

          <Divider />

          <Card title="测试流程说明">
            <Timeline>
              <Timeline.Item color="blue">
                <Text strong>1. Partner 服务商选择</Text>
                <br />
                <Text type="secondary">选择 WhatsApp 服务商：Twilio、Dialog360 或 AiSensy</Text>
              </Timeline.Item>
              <Timeline.Item color="cyan">
                <Text strong>2. Webhook 数据接收</Text>
                <br />
                <Text type="secondary">模拟不同服务商发送的 webhook 数据，包含用户消息</Text>
              </Timeline.Item>
              <Timeline.Item color="green">
                <Text strong>3. 账号映射路由</Text>
                <br />
                <Text type="secondary">根据 WhatsApp 账号查找对应的 Agent 映射关系</Text>
              </Timeline.Item>
              <Timeline.Item color="orange">
                <Text strong>4. Agent 平台调用</Text>
                <br />
                <Text type="secondary">调用配置的 Agent 平台的 API 接口</Text>
              </Timeline.Item>
              <Timeline.Item color="purple">
                <Text strong>5. 同步/异步处理</Text>
                <br />
                <Text type="secondary">根据映射配置选择处理模式</Text>
              </Timeline.Item>
              <Timeline.Item color="red">
                <Text strong>6. 响应发送</Text>
                <br />
                <Text type="secondary">将 Agent 响应发送回 WhatsApp 用户</Text>
              </Timeline.Item>
            </Timeline>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SendOutlined />
              发送消息到 WhatsApp
            </span>
          }
          key="send"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Card title="发送消息到 WhatsApp" size="small">
                <Form
                  form={sendForm}
                  layout="vertical"
                  onFinish={handleSendMessage}
                  size="small"
                >
                  <Form.Item
                    name="accountId"
                    label="WhatsApp 账号"
                  >
                    <Select placeholder="选择账号">
                      {accounts.map(account => (
                        <Select.Option key={account.id} value={account.id}>
                          {account.account_name} ({account.phone_number})
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="to"
                    label="接收者号码"
                    rules={[
                      { required: true, message: '请输入接收者号码' },
                      { pattern: /^\+?[\d\s\-()]+$/, message: '请输入有效的电话号码格式 (如: +8615002185018 或 1234567890)' }
                    ]}
                  >
                    <Input placeholder="输入接收者 WhatsApp 号码 (如: +8615002185018 或 1234567890)" />
                  </Form.Item>

                  <Form.Item
                    name="message"
                    label="消息内容"
                    rules={[{ required: true, message: '请输入消息内容' }]}
                  >
                    <TextArea
                      placeholder="输入要发送的消息内容..."
                      rows={6}
                      maxLength={5000}
                      showCount
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={sending}
                      icon={<SendOutlined />}
                      block
                    >
                      {sending ? '发送中...' : '发送消息'}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            <Col span={12}>
              <Card
                title={`发送历史 (${sendHistory.length})`}
                size="small"
                extra={
                  sendHistory.length > 0 && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setSendHistory([])}
                    >
                      清空历史
                    </Button>
                  )
                }
              >
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  {sendHistory.length === 0 ? (
                    <Text type="secondary">暂无发送记录</Text>
                  ) : (
                    sendHistory.map(record => (
                      <div key={record.id} style={{
                        padding: '8px',
                        marginBottom: '8px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '4px',
                        backgroundColor: record.status === 'success' ? '#f6ffed' :
                                       record.status === 'failed' ? '#fff2f0' : '#f0f9ff'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <Text strong style={{ fontSize: '12px' }}>
                            {record.timestamp}
                          </Text>
                          <Tag
                            color={
                              record.status === 'success' ? 'success' :
                              record.status === 'failed' ? 'error' :
                              record.status === 'sending' ? 'processing' : 'default'
                            }
                          >
                            {record.status === 'success' ? '成功' :
                             record.status === 'failed' ? '失败' :
                             record.status === 'sending' ? '发送中' : '未知'}
                          </Tag>
                        </div>
                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                          <Text type="secondary">接收者: </Text>
                          <Text>{record.to}</Text>
                        </div>
                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                          <Text type="secondary">消息: </Text>
                          <Text ellipsis={{ tooltip: record.message }}>
                            {record.message.length > 50 ? `${record.message.substring(0, 50)}...` : record.message}
                          </Text>
                        </div>
                        {record.error && (
                          <div style={{ fontSize: '12px' }}>
                            <Text type="danger">错误: {record.error}</Text>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          <Divider />

          <Card title="发送消息流程说明">
            <Timeline>
              <Timeline.Item color="blue">
                <Text strong>1. 选择发送账号</Text>
                <br />
                <Text type="secondary">从已配置的 WhatsApp 账号中选择用于发送消息的账号</Text>
              </Timeline.Item>
              <Timeline.Item color="green">
                <Text strong>2. 输入接收者信息</Text>
                <br />
                <Text type="secondary">输入目标用户的 WhatsApp 号码作为接收者，支持国际格式 (如: +8615002185018)</Text>
              </Timeline.Item>
              <Timeline.Item color="orange">
                <Text strong>3. 编写消息内容</Text>
                <br />
                <Text type="secondary">编写要发送的消息内容，最多支持 1000 个字符</Text>
              </Timeline.Item>
              <Timeline.Item color="purple">
                <Text strong>4. 执行发送</Text>
                <br />
                <Text type="secondary">点击发送按钮，通过 WhatsApp Business API 发送消息</Text>
              </Timeline.Item>
              <Timeline.Item color="red">
                <Text strong>5. 记录发送结果</Text>
                <br />
                <Text type="secondary">记录发送结果到历史记录中，支持状态跟踪</Text>
              </Timeline.Item>
            </Timeline>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default WhatsAppConnectorTestPage;
